import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { theme } from '../../../theme';
import { Card } from '../../ui/Card';
import { FiUsers, FiBriefcase, FiUserCheck, FiTrendingUp } from 'react-icons/fi';
import { useAuth } from '../../../contexts/AuthContext';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { StaffData } from '../../../firebase/auth';

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${theme.spacing[4]};
  margin-bottom: ${theme.spacing[6]};

  @media (max-width: ${theme.breakpoints.md}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const MetricCard = styled(Card)`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: ${theme.spacing[6]} ${theme.spacing[4]} !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid ${theme.colors.light.border.light};
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05), 0 1px 2px 0 rgba(0, 0, 0, 0.03) !important;

  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1) !important;
    border-color: ${theme.colors.primary[200]};
  }
`;

const MetricIcon = styled.div<{ color: string }>`
  width: 64px;
  height: 64px;
  border-radius: ${theme.borderRadius.xl};
  background: linear-gradient(135deg, ${({ color }) => color}20, ${({ color }) => color}10);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${theme.spacing[4]};
  transition: transform 0.3s ease;

  ${MetricCard}:hover & {
    transform: scale(1.1);
  }

  svg {
    color: ${({ color }) => color};
    font-size: 28px;
  }
`;

const MetricValue = styled.div`
  font-size: ${theme.typography.fontSize['3xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.light.text.primary};
  margin-bottom: ${theme.spacing[2]};
  line-height: 1;
  letter-spacing: -0.03em;
`;

const MetricLabel = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.light.text.secondary};
  font-weight: ${theme.typography.fontWeight.medium};
  line-height: 1.4;
`;

interface Metric {
  icon: React.ReactNode;
  label: string;
  value: string;
  color: string;
}

const StaffMetricCards: React.FC = () => {
  const { staffData, companyData } = useAuth();
  const [staffList, setStaffList] = useState<StaffData[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStaffData = async () => {
      if (!companyData?.id) {
        setLoading(false);
        return;
      }

      try {
        const staffRef = collection(db, 'companies', companyData.id, 'staff');
        const querySnapshot = await getDocs(staffRef);
        const staff: StaffData[] = [];
        querySnapshot.forEach((doc) => {
          staff.push(doc.data() as StaffData);
        });
        setStaffList(staff);
      } catch (error) {
        console.error('Error fetching staff:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStaffData();
  }, [companyData]);

  // Filter staff by department if staff member has a department
  const currentDepartment = staffData?.department || staffData?.departments?.[0];
  const departmentStaff = currentDepartment
    ? staffList.filter((s) => s.department === currentDepartment || s.departments?.includes(currentDepartment))
    : staffList;

  // Calculate metrics
  const totalStaff = departmentStaff.length;
  const totalDepartments = new Set(departmentStaff.map((s) => s.department).filter(Boolean)).size;
  // All staff are considered active (role type doesn't include 'inactive')
  const activeStaff = departmentStaff.length;
  const newThisMonth = departmentStaff.filter((s) => {
    if (!s.createdAt) return false;
    // Handle both Date and Firestore Timestamp
    let createdDate: Date;
    if (s.createdAt instanceof Date) {
      createdDate = s.createdAt;
    } else if (s.createdAt && typeof s.createdAt === 'object' && 'toDate' in s.createdAt) {
      createdDate = (s.createdAt as any).toDate();
    } else {
      return false;
    }
    const now = new Date();
    return createdDate.getMonth() === now.getMonth() && createdDate.getFullYear() === now.getFullYear();
  }).length;

  const metrics: Metric[] = [
    {
      icon: <FiUsers />,
      label: currentDepartment ? `Staff in ${currentDepartment}` : 'Total Staff',
      value: loading ? '...' : totalStaff.toString(),
      color: theme.colors.primary[500],
    },
    {
      icon: <FiBriefcase />,
      label: 'Departments',
      value: loading ? '...' : totalDepartments.toString(),
      color: theme.colors.primary[600],
    },
    {
      icon: <FiUserCheck />,
      label: 'Active Staff',
      value: loading ? '...' : activeStaff.toString(),
      color: theme.colors.primary[500],
    },
    {
      icon: <FiTrendingUp />,
      label: 'New This Month',
      value: loading ? '...' : newThisMonth.toString(),
      color: theme.colors.primary[600],
    },
  ];

  return (
    <MetricsGrid>
      {metrics.map((metric, index) => (
        <MetricCard key={index}>
          <MetricIcon color={metric.color}>{metric.icon}</MetricIcon>
          <MetricValue>{metric.value}</MetricValue>
          <MetricLabel>{metric.label}</MetricLabel>
        </MetricCard>
      ))}
    </MetricsGrid>
  );
};

export default StaffMetricCards;

